// src/components/Modals/QuestionBankModal.js
import React, { useState, useEffect } from "react";
import axios from "../../routers/axios";
import { X } from "lucide-react";
import AddQuestionComponent from "../add_question";

const QUESTION_TYPES = [
  "multiple_choice",
  "true_false",
  "true_false_not_given",
  "classification",
  "job_comparison",
  "matching",
  "synthesis_matrix",
  "short_answer",
];

export default function QuestionBankModal({ scenarioId, section, onClose }) {
  const [activeTab, setActiveTab] = useState("multiple_choice");
  const [questions, setQuestions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);

  // 🟢 Load danh sách câu hỏi theo loại
  useEffect(() => {
    loadQuestions();
  }, [activeTab]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/questions/list?question_type=${activeTab}`);
      setQuestions(res.data || []);
    } catch (err) {
      console.error("❌ Lỗi khi tải câu hỏi:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddToScenario = async () => {
    if (selected.length === 0) {
      alert("⚠️ Vui lòng chọn ít nhất một câu hỏi để thêm.");
      return;
    }
    try {
      await axios.post("/questions/add_to_scenario", {
        scenario_id: scenarioId,
        section,
        question_ids: selected,
      });
      alert("✅ Đã thêm câu hỏi vào scenario thành công!");
      onClose();
    } catch (err) {
      console.error("❌ Lỗi khi thêm câu hỏi:", err);
      alert("Không thể thêm câu hỏi. Vui lòng thử lại.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      {/* 🧩 Modal container */}
      <div
        className="bg-white w-[90vw] max-w-[1200px] h-[85vh] rounded-2xl shadow-2xl p-6 relative flex flex-col overflow-hidden"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {/* CSS ẩn scrollbar nhưng vẫn cuộn */}
        <style>{`
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

        {/* Header */}
        {!showAddQuestion && (
          <div className="flex justify-between items-center mb-4 flex-shrink-0">
            <h2 className="text-xl font-bold">Ngân hàng câu hỏi</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddQuestion(true)}
                className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                + Tạo câu hỏi
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Đóng"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Nội dung modal */}
        {showAddQuestion ? (
          // 🟢 Khi bấm "Tạo câu hỏi" → hiển thị form ngay trong modal
          <div className="flex-1 overflow-y-auto no-scrollbar">
            <AddQuestionComponent
              mode="create" // tạo riêng trong Bank
              questionType={activeTab}
              onClose={() => {
                setShowAddQuestion(false);
                loadQuestions();
              }}
            />
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto border-b pb-1 flex-shrink-0">
              {QUESTION_TYPES.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1 whitespace-nowrap rounded-t-md ${activeTab === tab
                    ? "border-b-2 border-blue-600 text-blue-600 font-semibold"
                    : "text-gray-600 hover:text-blue-600"
                    }`}
                >
                  {tab.replace(/_/g, " ")}
                </button>
              ))}
            </div>

            {/* Bảng câu hỏi */}
            <div className="flex-1 mt-3 overflow-y-auto no-scrollbar border rounded-lg pr-2">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="border px-2 py-1 text-left w-16">Chọn</th>
                    <th className="border px-2 py-1 text-left w-16">ID</th>
                    <th className="border px-2 py-1 text-left w-40">Loại</th>
                    <th className="border px-2 py-1 text-left">Câu hỏi</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center py-4 text-gray-500 italic"
                      >
                        Đang tải câu hỏi...
                      </td>
                    </tr>
                  ) : questions.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center py-4 text-gray-400 italic"
                      >
                        Không có câu hỏi nào thuộc loại này.
                      </td>
                    </tr>
                  ) : (
                    questions.map((q) => (
                      <tr
                        key={q.id}
                        className={`hover:bg-gray-50 ${selected.includes(q.id) ? "bg-blue-50" : ""
                          }`}
                      >
                        <td className="border px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={selected.includes(q.id)}
                            onChange={() => toggleSelect(q.id)}
                          />
                        </td>
                        <td className="border px-2 py-1">{q.id}</td>
                        <td className="border px-2 py-1 capitalize">
                          {q.question_type}
                        </td>
                        <td className="border px-2 py-1">{q.question_text}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center mt-4 flex-shrink-0">
              <p className="text-sm text-gray-500">
                Đang chọn: <b>{selected.length}</b> câu hỏi
              </p>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                onClick={handleAddToScenario}
              >
                Thêm vào scenario ({selected.length})
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
